<template>
	<VDataGridV2
		id="transportable-documents-registry"
		:loader="dataLoader"
		:entity-type="'transportOrder_' + transportOrder?.type"
		sticky
		paginated
		striped
		:layout="layout"
		:show-context-menu-column="showContextMenuColumn"
		:skeleton-row-count="20"
		:selection-control="selectionControl"
		:selectable="isSelectable"
		:selected-rows="selectedRows"
		:disable-row-when="hasTransportOrder ? hasTemperatureRangeConflict : null"
		:context-menu="TransportableDocumentsService.contextMenu"
		@row:dblclick="TransportableDocumentsService.edit"
		@row:selected="onRowSelected"
		@row:deselected="onRowDeselected"
		@update:selected-rows="onSelectedRowsUpdate"
		@update:selectable="onSelectableUpdate"
	>
		<VDataGridColumnV2 id="id" label="№" width="100px" content-align="left">
			<template #default="{ row }">
        <VLink
					@click.prevent.stop="() => TransportableDocumentsService.edit(row)"
        >
					{{ $core.utils.string.formatWithDelimiter(row.id) }}
        </VLink>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="documentClass"
			width="80px"
			content-align="left"
			label="Документ"
			content-class="ellipsis"
		>
			<template #default="{ row }">
				{{ row.documentClassStringRepresentation }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="sellerName"
			label="От кого"
			sortable
			width="150px"
			content-class="ellipsis"
		>
			<template #default="{ row }">
				{{ row.sender?.name }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="buyerName"
			label="Кому"
			sortable
			width="150px"
			content-class="ellipsis"
		>
			<template #default="{ row }">
				{{ row.receiver?.name }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="senderTransportZone"
			label="Откуда"
			width="100px"
			content-class="ellipsis"
		>
			<template #default="{ row }">
				{{ row.senderTransportZone?.name || '--' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="receiverTransportZone"
			label="Куда"
			width="100px"
			content-class="ellipsis"
		>
			<template #default="{ row }">
				{{ row.receiverTransportZone?.name || '--' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="cargoWeight"
			label="Вес брутто, кг"
      header-class="nowrap"
			width="100px"
			content-align="right"
			content-class="ellipsis"
		>
			<template #default="{ row }">
				{{ row.cargoWeightGross ?? '--' }} кг
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="cargoVolume"
			label="Объём, м³"
      header-class="nowrap"
      width="100px"
			content-align="right"
			content-class="ellipsis"
		>
			<template #default="{ row }">
				{{ row.cargoVolume ?? '--' }} м³
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="storeTemperatureRange"
			label="Темп режим, °C"
		>
			<template #default="{ row }">
				{{ row.storeTemperatureRange?.toString({ unit: '°C' }) || '--' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="readiness"
			label="Готовность"
			hint="У ЗТК - дата планового получения;

			 У отгрузок - планируемая дата прибытия (доставка покупателем) / дата отгрузки (доставка поставщиком)"
			sortable
			sort-default-value="desc"
			width="150px"
			content-class="ellipsis"
		>
			<template #default="{ row }">
				{{ row.arrivalDatePlanned || '--' }}
			</template>
		</VDataGridColumnV2>

		<!-- Action-bar только в обычном режиме выбора (без ЗТК-приёмника) -->
		<VDataGridActionBarV2 v-if="!hasTransportOrder" #default="{ selectedRows: barSelectedRows }">
			<div class="chosen-position">
				<VButton v-if="selectable" @click="cancelSelect">Отменить выбор</VButton>
				<VButton v-else @click="selectable = true">Выбрать позиции</VButton>
			</div>

			<template v-if="selectable"> Выбрано {{ barSelectedRows.length }}</template>
			<TransportOrderCreateButtons
        :selected-documents="barSelectedRows"
        @transport-order:created="refresh"
      />
		</VDataGridActionBarV2>
	</VDataGridV2>
</template>

<script lang="ts">
import Utils                             from '@/core/utils/Utils';
import TransportableDocumentsContextMenu from '@/domains/TransportableDocuments/components/TransportableDocumentsRegistry/TransportableDocumentsContextMenu.vue';
import TransportOrder                    from '@/domains/TransportOrders/TransportOrder';
import TransportOrderPosition            from '@/domains/TransportOrders/TransportOrderPosition';
import TransportOrdersService            from '@/domains/TransportOrders/TransportOrdersService';
import TransportableDocumentsService     from '@/domains/TransportableDocuments/TransportableDocumentsService';
import TransportOrderCreateButtons       from '@/domains/TransportOrders/components/TransportOrderCreateButtons.vue';
import DataGridLoader                    from '@/lib/components/VDataGridV2/DataGridLoader';

export default defineComponent({
	name: 'TransportableDocumentsRegistry',

	components: {
		TransportableDocumentsContextMenu,
		TransportOrderCreateButtons,
	},

	props: {
		params: {
			type:    Object as PropType<Parameters<typeof TransportOrdersService.getTransportableDocumentsPaginated>[0]>,
			default: () => ({}),
		},

		/**
		 * ЗТК-приёмник. Если передан — реестр работает в режиме наполнения:
		 * выбор документа сразу добавляет его в этот TransportOrder через addPosition,
		 * снятие выбора — удаляет позицию. Выбранные строки выводятся из позиций ЗТК.
		 * Если не передан — обычный режим выбора через selectedShipmentDocuments.
		 */
		transportOrder: {
			type:    Object as PropType<TransportOrder>,
			default: null,
		},

		/**
		 * Режим контрола выбора строк.
		 * По умолчанию 'checkbox'. Для поведения старого пикера передать 'action'.
		 */
		selectionControl: {
			type:    String as PropType<'checkbox' | 'action' | 'none'>,
			default: 'checkbox',
		},

		// ====== Конфигурация столбцов ======
		showContextMenuColumn: {
			type: Boolean,
			default: true,
		},

		// ====== Расположение таблицы ======
		layout: {
			type: String as PropType<'fill' | 'auto' | 'left'>,
			default: 'auto',
			validator: (v: string) => ['fill', 'auto', 'left'].includes(v),
		},
	},

	setup() {
		return {
			TransportOrdersService,
			TransportableDocumentsService,
		};
	},

	data() {
		return {
			selectable: false,
			selectedShipmentDocuments: [] as any[],

			contextMenu: {} as Record<string | number, boolean>,
			dataLoader: null as DataGridLoader,
		};
	},

	created() {
		this.dataLoader = new DataGridLoader(TransportOrdersService.getTransportableDocumentsPaginated)
			.useReactiveSource(() => this.params);
	},

	computed: {
		/** true, когда передан transportOrder — реестр пишет выбор прямо в эту ЗТК */
		hasTransportOrder(): boolean {
			if (this.transportOrder) {
				return true;
			} else {
				return false;
			}
		},

		/**
		 * Выбор включён всегда при наличии ЗТК-приёмника,
		 * в обычном режиме управляется кнопкой (selectable).
		 */
		isSelectable(): boolean {
			if (this.hasTransportOrder) {
				return true;
			} else {
				return this.selectable;
			}
		},

		/**
		 * Выбранные строки.
		 * При наличии ЗТК выводятся из позиций (источник истины — сама ЗТК),
		 * иначе — из локального массива selectedShipmentDocuments.
		 */
		selectedRows(): any[] {
			if (this.hasTransportOrder) {
				return this.transportOrder.positions.map(position => position.document);
			} else {
				return this.selectedShipmentDocuments;
			}
		},
	},

	methods: {
		refresh(): void {
			this.dataLoader.refresh();
		},

		onSelectableUpdate(value) {
			this.selectable = value;
		},

		cancelSelect(): void {
			this.selectable = false;
			this.selectedShipmentDocuments = [];
		},

		// ==================== Выбор строки ====================

		onRowSelected(document: any): void {
			if (this.hasTransportOrder) {
				this.addDocument(document);
			} else {
				// в обычном режиме набор ведёт грид через update:selected-rows
			}
		},

		onRowDeselected(document: any): void {
			if (this.hasTransportOrder) {
				this.removeDocument(document);
			} else {
				// в обычном режиме набор ведёт грид через update:selected-rows
			}
		},

		onSelectedRowsUpdate(rows: any[]): void {
			if (this.hasTransportOrder) {
				// при наличии ЗТК набор выводится из позиций, локальный массив не ведём
			} else {
				this.selectedShipmentDocuments = rows;
			}
		},

		onShipmentDocumentSelected(shipmentDocument: any): void {
			if (this.selectable === false) {
				this.selectable = true;
			}

			this.selectedShipmentDocuments.push(shipmentDocument);
		},

		// ==================== Наполнение ЗТК ====================

		async addDocument(document: any): Promise<void> {
			const fullDocument = await TransportableDocumentsService[document.documentClass].get(document.id);
			if (!fullDocument) return;

			const position = TransportOrderPosition.fromDocument(fullDocument);
			this.transportOrder.addPosition(position);
		},

		removeDocument(document: any): void {
			const position = this.transportOrder.positions.find(p => p.document?.id === document.id);
			if (!position) return;

			this.transportOrder.deletePosition(position);
		},

		// ==================== Температурный конфликт ====================

		/**
		 * Проверяет совместимость температурного режима документа с ЗТК.
		 * Возвращает строку-причину при конфликте (для тултипа disabled-строки),
		 * либо false, если конфликта нет.
		 */
		hasTemperatureRangeConflict(document: any): string | boolean {
			const documentRange = document.storeTemperatureRange;
			if (!documentRange) return false;

			const orderRange = this.transportOrder.storeTemperatureRange;
			if (!orderRange) return false;

			const intersection = Utils.number.numberIntervalIntersection([orderRange, documentRange]);
			if (!intersection) return false;

			if (intersection.end - intersection.start < 0) {
				return 'Температурный режим документа несовместим с режимом ЗТК';
			} else {
				return false;
			}
		},
	},
});
</script>
